import * as React from 'react';
import Footer from 'app/components/FooterComponent';
import Header from 'app/components/HeaderComponent';
import HeaderImage from 'home/components/HeaderImage';
import { Alert, Button, TextField, InputAdornment, IconButton, Box, Typography, Divider } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import PersonIcon from '@mui/icons-material/Person';
import { clearLoginError, USER_LOGIN_REQUEST } from 'actions';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import config from 'config';
import AuthLayout from '_containers/AuthLayout';

type Props = {
  user: any,
  dispatch: Function,
  history?: any,
};

type State = {
  username: string,
  password: string,
  showPassword: boolean,
  touched: boolean,
};

export default class LoginPage extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      username: '',
      password: '',
      showPassword: false,
      touched: false,
    };
    if (config.loginPageRedirection) {
      window.location = config.loginPageRedirection;
    }
  }

  componentWillUnmount() {
    const { dispatch } = this.props;
    dispatch(clearLoginError());
  }

  isEmailValid = () => {
    const { username } = this.state;
    return /^\S+@\S+\.\S+$/.test(username.trim());
  }

  canSubmit = () => {
    const { password } = this.state;
    const { user } = this.props;
    return this.isEmailValid() && password.length > 0 && !user.isRunning;
  }

  submitLogin = () => {
    const { dispatch } = this.props;
    const { username, password } = this.state;
    this.setState({ touched: true });

    if (!this.canSubmit()) {
      return;
    }

    dispatch(USER_LOGIN_REQUEST(username.trim(), password));
  }

  handleKeyPressOnFormInput = (e) => {
    if (e.key === 'Enter') {
      this.submitLogin();
    }
  }

  togglePasswordVisibility = () => {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  }

  loginError = () => {
    const { user: { loginError } } = this.props;

    if (loginError) {
      return <Alert severity="error" className="auth-alert">{loginError}</Alert>;
    }

    return null;
  }

  loginForm() {
    const { user } = this.props;
    const { username, password, showPassword, touched } = this.state;
    const showEmailError = touched && !this.isEmailValid();
    const showPasswordError = touched && password.length === 0;

    return (
      <Box component="form" id="login-form" className="auth-form" noValidate>
        {this.loginError()}
        <Box className="auth-field">
          <label htmlFor="username">Email address</label>
          <TextField
            id="username"
            type="email"
            placeholder="your.email@example.com"
            fullWidth
            size="small"
            variant="outlined"
            className="auth-input"
            value={username}
            error={showEmailError}
            helperText={showEmailError ? 'Enter a valid email address.' : undefined}
            onKeyDown={this.handleKeyPressOnFormInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ username: event.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon className="auth-input-icon" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Box className="auth-field">
          <label htmlFor="password">Password</label>
          <TextField
            id="password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            fullWidth
            size="small"
            variant="outlined"
            className="auth-input"
            value={password}
            error={showPasswordError}
            helperText={showPasswordError ? 'Password is required.' : undefined}
            onKeyDown={this.handleKeyPressOnFormInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.setState({ password: event.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon className="auth-input-icon" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={this.togglePasswordVisibility}
                    edge="end"
                    className="auth-visibility-toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Button
          variant="contained"
          type="button"
          fullWidth
          size="large"
          className="auth-primary-button"
          id="login_button"
          startIcon={<LoginIcon />}
          disabled={user.isRunning}
          onClick={this.submitLogin}
        >
          {user.isRunning ? 'Signing in...' : <FormattedMessage id="login" defaultMessage="Log in" />}
        </Button>
      </Box>
    );
  }

  content = () => {
    return (
      <AuthLayout
        title="Welcome back"
        subtitle="Sign in with the email on your account"
        brandDescription="Sign in to pick up your latest project and keep your BOM in sync."
        avatarIcon={<PersonIcon />}
        cardClassName="auth-layout--login"
      >
        {this.loginForm()}
        {this.links()}
      </AuthLayout>
    );
  }

  links = () => {
    return (
      <Box className="auth-links auth-links--login">
        <Link to="/forgotpassword" className="auth-link auth-forgot-link">
          Forgot password?
        </Link>
        <Divider className="auth-divider" />
        <Box className="auth-account-prompt">
          <Typography variant="body2" component="span" className="auth-prompt-text">
            New to U-Builder?{' '}
          </Typography>
          <Link to="/registration" className="auth-link">
            Create account
          </Link>
        </Box>
      </Box>
    );
  }

  render() {
    return (
      <div className="app-content login-page auth-page">
        <Header />
        <HeaderImage content={this.content()} />
        <Footer />
      </div>
    );
  }
}
